import { format } from "date-fns";
import { MoreHorizontal } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { formatTime } from "@/lib/api";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};
const ACTIONS = [["confirmed", "Confirm"], ["completed", "Mark completed"], ["cancelled", "Cancel"], ["pending", "Back to pending"]];

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[status]}`}>{status}</span>
);

export const AppointmentsTable = ({ items, onStatus, onDelete }) => {
  if (!items.length) return <p className="py-16 text-center text-muted-foreground" data-testid="appointments-empty">No appointments match these filters.</p>;
  return (
    <Table data-testid="appointments-table">
      <TableHeader>
        <TableRow>
          <TableHead>Patient</TableHead><TableHead>Date & time</TableHead><TableHead>Location</TableHead><TableHead className="hidden md:table-cell">Reason</TableHead>
          <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((a) => (
          <TableRow key={a.id} data-testid={`appointment-row-${a.reference}`}>
            <TableCell>
              <p className="font-medium text-[#1A252C]">{a.name}</p>
              <p className="text-xs text-muted-foreground">{[a.age != null && `${a.age} yrs`, a.gender].filter(Boolean).join(", ")}</p>
              <p className="text-xs text-muted-foreground">
                <a href={`tel:+91${a.phone}`} className="hover:text-primary">{a.phone}</a> · {a.reference}{a.source === "walk-in" ? " · Walk-in" : ""}
              </p>
            </TableCell>
            <TableCell className="whitespace-nowrap">
              <p>{format(new Date(`${a.date}T00:00:00`), "d MMM yyyy")}</p>
              <p className="text-xs text-muted-foreground">{formatTime(a.time)}</p>
            </TableCell>
            <TableCell className="text-sm" data-testid={`appointment-location-${a.reference}`}>{a.clinic === "online" ? "Online" : a.clinic_name}</TableCell>
            <TableCell className="hidden md:table-cell max-w-xs truncate text-muted-foreground">{a.reason || "—"}</TableCell>
            <TableCell data-testid={`appointment-status-${a.reference}`}><StatusBadge status={a.status} /></TableCell>
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid={`appointment-actions-${a.reference}`}><MoreHorizontal className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {ACTIONS.filter(([s]) => s !== a.status).map(([s, label]) => (
                    <DropdownMenuItem key={s} onClick={() => onStatus(a.id, s)} data-testid={`action-${s}-${a.reference}`}>{label}</DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(a)} data-testid={`action-delete-${a.reference}`}>Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
